/**
 * 3D Real Estate House Designer Project Data
 * Web-based 3D house model designer with real-time cost calculation
 */

import type { Project, Task, Epic, Sprint } from '../types';

const ORG_ID = 'org_intellesphere';
const PROJECT_ID = 'proj-3d-house-designer';

export const houseDesignerProject: Project = {
  id: PROJECT_ID,
  organizationId: ORG_ID,
  name: 'RealEstate 3D House Designer Platform',
  description: 'Web-based 3D house model designer where customers can customize floor plans, materials, finishes, and furniture. Real-time cost calculation based on selections with photo-realistic rendering.',
  status: 'Approved',
  proposal: `# RealEstate 3D House Designer Platform

## Overview
Build an interactive web-based 3D house designer that allows customers to visualize, customize, and get real-time cost estimates for their dream home.

## Key Features
- **3D Visualization**: Interactive Three.js-powered 3D house models
- **Floor Plan Editor**: Drag-and-drop room layout customization
- **Material Selection**: Thousands of flooring, countertop, paint, and fixture options
- **Furniture Catalog**: Pre-designed furniture sets and individual pieces
- **Cost Calculator**: Real-time cost updates as customers make changes
- **Photo-Realistic Rendering**: High-quality renders for presentations
- **PDF Export**: Detailed design docs with materials list and costs
- **QuickBooks Integration**: Export estimates directly to QuickBooks

## Technology Stack
- **3D Engine**: Three.js + React Three Fiber
- **Frontend**: Next.js 14, TypeScript, Tailwind CSS
- **Backend**: Node.js + Express
- **Database**: PostgreSQL + Redis cache
- **Storage**: S3 for 3D models and renders
- **Rendering**: Cloud rendering service (AWS Batch)

## Target Markets
- Real estate developers
- Custom home builders
- Interior designers
- Homeowners planning renovations

## Timeline
12 months to MVP`,
  costs: [
    { id: 'cost-hd-dev', description: 'Development Team (6 engineers × 12 months)', amount: 720000, category: 'Personnel' },
    { id: 'cost-hd-3d', description: '3D Modelers & Designers (2 FTE × 12 months)', amount: 180000, category: 'Personnel' },
    { id: 'cost-hd-infra', description: 'AWS Infrastructure & Rendering', amount: 60000, category: 'Infrastructure' },
    { id: 'cost-hd-models', description: '3D Model Library Purchase', amount: 50000, category: 'Assets' },
  ],
  teamMembers: [
    { userId: 'hossainzarif2002@gmail.com', role: 'developer' },
    { userId: 'jesin.shah', role: 'developer' },
    { userId: 'asif.redmond@gmail.com', role: 'stakeholder' },
    { userId: 'aidimentry', role: 'developer' },
  ],
  createdAt: '2026-02-23T00:00:00Z',
  updatedAt: '2026-02-23T00:00:00Z',
  externalLinks: [],
  startDate: '2026-04-01',
  targetEndDate: '2027-03-31',
  costPerHour: 100,
};

export const houseDesignerEpics: Epic[] = [
  {
    id: 'epic-hd-3dengine',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: '3D Engine & Rendering',
    description: 'Three.js integration, camera controls, lighting, shadows, rendering pipeline',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    costPerHour: 100,
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
  },
  {
    id: 'epic-hd-floorplan',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Floor Plan Editor',
    description: 'Drag-and-drop floor plan builder, room templates, wall customization',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    costPerHour: 100,
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
  },
  {
    id: 'epic-hd-materials',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Material & Finish Database',
    description: 'Material library, PBR textures, vendor integrations, pricing database',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    costPerHour: 100,
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
  },
  {
    id: 'epic-hd-furniture',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Furniture & Decor Catalog',
    description: '3D furniture models, placement tools, style collections',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    costPerHour: 100,
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
  },
  {
    id: 'epic-hd-cost',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Cost Calculation Engine',
    description: 'Real-time cost calculator, material pricing, labor estimates, regional adjustments',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    costPerHour: 100,
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
  },
  {
    id: 'epic-hd-export',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Export & Integration',
    description: 'PDF export, QuickBooks integration, AutoCAD export, sharing features',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    costPerHour: 100,
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
  },
];

export const houseDesignerTasks: Task[] = [
  {
    id: 'HD-001',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-hd-3dengine',
    title: 'Three.js Scene Setup & Camera Controls',
    description: 'Initialize Three.js scene, implement orbit controls, camera positioning, and viewport management',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Medium',
    category: '3D Development',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 5,
    estimatedHours: 40,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'HD-002',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-hd-3dengine',
    title: 'PBR Material System & Lighting',
    description: 'Implement physically-based rendering materials, realistic lighting with shadows and reflections',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'High',
    category: '3D Development',
    assigneeIds: [],
    parentTaskIds: ['HD-001'],
    subtaskIds: [],
    estimatedDays: 8,
    estimatedHours: 64,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'HD-003',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-hd-3dengine',
    title: 'Cloud-Based Photo-Realistic Rendering',
    description: 'Integrate AWS Batch for high-quality ray-traced rendering, queue management',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    complexity: 'High',
    category: 'Infrastructure',
    assigneeIds: [],
    parentTaskIds: ['HD-002'],
    subtaskIds: [],
    estimatedDays: 10,
    estimatedHours: 80,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'HD-010',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-hd-floorplan',
    title: 'Drag-and-Drop Floor Plan Builder',
    description: 'Interactive floor plan editor with room creation, wall drawing, and dimension tools',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'High',
    category: 'Frontend',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 12,
    estimatedHours: 96,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'HD-011',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-hd-floorplan',
    title: 'Room Templates Library',
    description: 'Pre-designed room templates (bedroom, kitchen, bathroom) with customization options',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    complexity: 'Medium',
    category: 'Frontend',
    assigneeIds: [],
    parentTaskIds: ['HD-010'],
    subtaskIds: [],
    estimatedDays: 6,
    estimatedHours: 48,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'HD-020',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-hd-materials',
    title: 'Material Library Database',
    description: 'PostgreSQL database for materials with categories, vendors, pricing, and PBR textures',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Medium',
    category: 'Backend',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 5,
    estimatedHours: 40,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'HD-021',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-hd-materials',
    title: 'Material Selection UI',
    description: 'Browse, search, and apply materials to 3D surfaces with preview',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Medium',
    category: 'Frontend',
    assigneeIds: [],
    parentTaskIds: ['HD-020'],
    subtaskIds: [],
    estimatedDays: 7,
    estimatedHours: 56,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'HD-030',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-hd-furniture',
    title: '3D Furniture Model Catalog',
    description: 'Curate and import 1000+ furniture 3D models optimized for web',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Medium',
    category: '3D Assets',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 15,
    estimatedHours: 120,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'HD-031',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-hd-furniture',
    title: 'Furniture Placement Tool',
    description: 'Drag-and-drop furniture placement with collision detection and snap-to-grid',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'High',
    category: 'Frontend',
    assigneeIds: [],
    parentTaskIds: ['HD-030'],
    subtaskIds: [],
    estimatedDays: 8,
    estimatedHours: 64,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'HD-040',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-hd-cost',
    title: 'Real-Time Cost Calculator',
    description: 'Calculate total cost based on materials, labor, room sizes, and regional factors',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Critical',
    complexity: 'High',
    category: 'Backend',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 10,
    estimatedHours: 80,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'HD-041',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-hd-cost',
    title: 'Material Pricing Integration',
    description: 'Integrate with vendor APIs for real-time material pricing and availability',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'High',
    category: 'Integration',
    assigneeIds: [],
    parentTaskIds: ['HD-040'],
    subtaskIds: [],
    estimatedDays: 8,
    estimatedHours: 64,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'HD-050',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-hd-export',
    title: 'PDF Export with Materials List',
    description: 'Generate professional PDF reports with 3D renders, floor plans, and itemized costs',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Medium',
    category: 'Backend',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 6,
    estimatedHours: 48,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'HD-051',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-hd-export',
    title: 'QuickBooks Integration',
    description: 'Export estimates and invoices directly to QuickBooks Online',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    complexity: 'Medium',
    category: 'Integration',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 5,
    estimatedHours: 40,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
];
